import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dictionary } from '@onaio/utils';
import * as Plot from '@observablehq/plot'; // Import Observable Plot
import * as d3 from 'd3'; // Import D3
import { LoadingOutlined } from '@ant-design/icons';
import useDeepCompareEffect from 'use-deep-compare-effect'; // Import deep compare effect

import { getComponentById, getComponentSource } from '../../../reducers/selectors/post';
import { PlotComponent } from '../../../configs/component-types';
import { GenericComponent } from '../Component';
import { useFetchComponentData } from '../hooks';
import { getContextRow } from '../helpers';
import { CHART_DEFAULT_QUERY_LIMIT } from '../../../configs/constants'; // Reuse chart limit for now

import './style.css'; // We'll need to create this file

/** Selector factories */
const makeGetComponentById = () => getComponentById;
const makeGetComponentSource = () => getComponentSource;

/** Interface for component props */
export interface PlotProps {
  componentIndex: number;
  componentId: string;
  filterByValue?: string; // Similar to ChartProps, might not be used initially
  dataRow?: Dictionary;
  cardIndex?: number;
  isEmbed?: boolean;
}

// Rate limiting implementation
const rateLimiter = new Map<string, number>();
const RATE_LIMIT_MS = 10; // 1 second between executions

const checkRateLimit = (componentId: string): boolean => {
  const lastExecution = rateLimiter.get(componentId);
  const now = Date.now();
  if (lastExecution && now - lastExecution < RATE_LIMIT_MS) {
    return false;
  }
  
  rateLimiter.set(componentId, now);
  return true;
};

// Add type declaration for performance.memory
declare global {
  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

// Memory usage monitoring
const checkMemoryUsage = (): boolean => {
  if (typeof performance !== 'undefined' && performance.memory) {
    const memoryLimit = 50 * 1024 * 1024; // 50MB
    return performance.memory.usedJSHeapSize < memoryLimit;
  }
  return true;
};

// Create a strict sandboxed environment for plot execution
const createPlotSandbox = (Plot: any, data: any[], d3: any) => {
  // Create a proxy to intercept property access
  const createSafeProxy = (obj: any) => {
    return new Proxy(obj, {
      get: (target, prop) => {
        // Only allow access to known safe properties
        if (typeof prop === 'string' && !prop.startsWith('_')) {
          const value = target[prop];
          if (typeof value === 'function') {
            return (...args: any[]) => {
              try {
                return value.apply(target, args);
              } catch (error) {
                throw new Error(`Execution error in ${prop}`);
              }
            };
          }
          return value;
        }
        return undefined;
      }
    });
  };

  return {
    Plot: createSafeProxy(Plot),
    data: Object.freeze(data),
    d3: createSafeProxy(d3)
  };
};

// Enhanced validation function
const validatePlotConfig = (config: string): { isValid: boolean; error?: string } => {
  // 1. Check for dangerous patterns
  const dangerousPatterns = [
    /eval\s*\(/i,
    /Function\s*\(/i,
    /setTimeout\s*\(/i,
    /setInterval\s*\(/i,
    /fetch\s*\(/i,
    /XMLHttpRequest/i,
    /WebSocket/i,
    /Worker\s*\(/i,
    /import\s*\(/i,
    /require\s*\(/i,
    /process\./i,
    /window\./i,
    /document\./i,
    /localStorage/i,
    /sessionStorage/i,
    /indexedDB/i,
    /navigator\./i,
    /location\./i,
    /history\./i,
    /console\.(?!log|error|warn)/i, // Only allow console.log, error, warn
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(config)) {
      return {
        isValid: false,
        error: `Plot configuration contains forbidden pattern: ${pattern.toString()}`,
      };
    }
  }

  // 2. Check for direct DOM manipulation
  const domManipulationPatterns = [
    /\.innerHTML/i,
    /\.outerHTML/i,
    /\.appendChild/i,
    /\.insertBefore/i,
    /\.removeChild/i,
    /\.style\s*=/i,
  ];

  for (const pattern of domManipulationPatterns) {
    if (pattern.test(config)) {
      return {
        isValid: false,
        error: 'Plot configuration contains direct DOM manipulation which is not allowed',
      };
    }
  }

  // 3. Check for return statement
  if (!config.includes('return')) {
    return {
      isValid: false,
      error: 'Plot configuration must include a return statement',
    };
  }

  // 4. Check for maximum code length
  if (config.length > 10000) { // Adjust limit as needed
    return {
      isValid: false,
      error: 'Plot configuration exceeds maximum length',
    };
  }

  // 5. Basic syntax validation
  try {
    new Function(config);
  } catch (error: any) {
    return {
      isValid: false,
      error: `Invalid JavaScript syntax: ${error?.message || 'Unknown error'}`,
    };
  }

  return { isValid: true };
};

// Execute plot with timeout
const executePlotWithTimeout = async (
  plotConfig: string,
  sandbox: any,
  timeoutMs: number = 5000
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Plot execution timed out'));
    }, timeoutMs);

    try {
      const plotFunction = new Function(
        'Plot',
        'data',
        'd3',
        'console',
        plotConfig
      );

      const result = plotFunction(
        sandbox.Plot,
        sandbox.data,
        sandbox.d3,
        sandbox.console
      );

      clearTimeout(timeoutId);
      resolve(result);
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
};

const PlotComp: React.FC<PlotProps> = React.memo((props: PlotProps) => {
  // Memoize selectors
  const selectComponentById = useMemo(makeGetComponentById, []);
  const selectComponentSource = useMemo(makeGetComponentSource, []);

  const { componentIndex, dataRow, cardIndex, isEmbed, componentId } = props;

  const component = useSelector((state: any) => {
    const plot = selectComponentById(state, { componentId });
    if (!plot) {
      return undefined;
    }
    return plot as PlotComponent;
  });

  /* @ts-ignore Should be Source | undefined, handle potentially missing source */
  const source = useSelector((state: any) => selectComponentSource(state, { componentId }));

  const [isLoadingData, rawData] = useFetchComponentData(
    componentId,
    component?.limit ? Number(component.limit) : CHART_DEFAULT_QUERY_LIMIT,
    cardIndex
  );

  const [plotOutput, setPlotOutput] = useState<HTMLElement | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const plotContainerRef = useRef<HTMLDivElement>(null);

  // Get filtered data based on context
  const data = useMemo(() => {
    if (!component) return [];
    const { filteredData } = getContextRow(dataRow, rawData, component, true);
    return filteredData.map((row: any) => {
      const mapped: any = {};
      Object.entries(row).forEach(([key, value]) => {
        const match = key.match(/\.([^.]+)$/);
        if (match) {
          mapped[match[1]] = value;
        } else {
          mapped[key] = value;
        }
      });
      return mapped;
    });
  }, [dataRow, rawData, component]);

  // Execute plot with security measures
  useDeepCompareEffect(() => {
    if (!component?.plotConfig || isLoadingData) {
      return;
    }

    const executePlot = async () => {
      try {
        setExecutionError(null);

        // Check rate limit
        // if (!checkRateLimit(componentId)) {
        //   throw new Error('Rate limit exceeded. Please wait before trying again.');
        // }

        // Check memory usage
        // if (!checkMemoryUsage()) {
        //   throw new Error('Memory usage limit exceeded.');
        // }

        // Validate the plot configuration
        const validation = validatePlotConfig(component.plotConfig);
        if (!validation.isValid) {
          throw new Error(validation.error);
        }

        // Create sandboxed environment
        const sandbox = createPlotSandbox(Plot, data, d3);

        // Execute with timeout
        const result = await executePlotWithTimeout(
          component.plotConfig,
          sandbox,
          5000 // 5 second timeout
        );

        // Validate the result
        if (result && (typeof result.render === 'function' || 
            (result instanceof Node && result.nodeType === Node.ELEMENT_NODE))) {
          setPlotOutput(result);
        } else {
          throw new Error('Plot configuration did not produce a valid output');
        }

      } catch (error: any) {
        console.error("Error executing plot config:", error);
        setExecutionError(`Error executing plot script: ${error.message}`);
        setPlotOutput(null);
      }
    };

    executePlot();
  }, [component?.plotConfig, data, isLoadingData, componentId]);

  // Effect to render the plot output into the container div
  useEffect(() => {
    const container = plotContainerRef.current;
    if (container) {
      // Clear previous content
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Append the new plot output if it exists
      if (plotOutput) {
        try {
          container.appendChild(plotOutput);
        } catch (error) {
          console.error("Error appending plot to container:", error);
          setExecutionError("Error rendering the plot.");
        }
      }
    }
  }, [plotOutput]);

  if (!component) {
    return null;
  }

  const genericComponentProps = {
    componentIndex,
    componentId,
    dataRow,
    isChart: false,
    drawerTitle: component.title || 'Plot',
    isEmbed: isEmbed,
  };

  return (
    <GenericComponent {...genericComponentProps}>
      {(isLoadingData) && (
        <div className="component-loader">
          <LoadingOutlined /> Loading data...
        </div>
      )}
      {executionError && (
        <div className="component-error">
          Error: {executionError}
        </div>
      )}
      <div 
        ref={plotContainerRef} 
        className="plot-container" 
        style={{ 
          background: component.backgroundColor || 'transparent', 
          minHeight: '50px'
        }}
      />
    </GenericComponent>
  );
});

PlotComp.displayName = 'Plot';
export { PlotComp as Plot }; 